import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsPlusCircle } from 'react-icons/bs';
import { useAuthStore } from '../../store/authStore';
import StatusFilterTabs, { type StatusFilterTab } from '../../components/shared/StatusFilterTabs';
import DeleteConfirmDialog from '../../components/shared/DeleteConfirmDialog';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { showToast } from '../../store/toastStore';
import {
  createRoutingRuleColumnDefs,
  createSimulationStepColumnDefs,
  type RoutingRuleGridContext,
} from './table/columnDefs';
import {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  ROUTING_GRID_DEFAULT_COL_DEF,
  routingRuleGridOptions,
  simulationStepGridOptions,
} from './table/gridOptions';
import { RULE_STATUS_TABS, SEARCH_DEBOUNCE_MS, type RuleStatusTab } from './constants/constants';
import { filterRoutingRules } from './utils/utils';
import {
  createRoutingRule,
  deleteRoutingRule,
  fetchActiveMerchants,
  fetchRiskFields,
  fetchRoutingRules,
  setRoutingRuleActive,
  simulateRouting,
  updateRoutingRule,
} from './api/api';
import type { RiskField, RoutingRule, RoutingRulePayload, RoutingSimulation } from './models/types';
import RoutingRuleDialog from './components/RoutingRuleDialog';
import RoutingRuleViewDialog from './components/RoutingRuleViewDialog';

type TabKey = 'rules' | 'simulator';

const PAYMENT_ROUTING_VIEW_TABS = [
  { value: 'rules', label: 'Routing Rules' },
  { value: 'simulator', label: 'Simulator' },
] as const;

const SIM_DEVICE_OPTIONS = [
  { value: 'ANY', label: 'Any' },
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'DESKTOP', label: 'Desktop' },
  { value: 'TABLET', label: 'Tablet' },
] as const;

export interface MerchantOption {
  id: number;
  name: string;
}

export default function PaymentRouting() {
  const userType = useAuthStore((state) => state.user?.userType);
  const isMerchantUser = userType === "MERCHANT";
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [merchantsFailed, setMerchantsFailed] = useState(false);
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("rules");
  const [ruleStatusTab, setRuleStatusTab] = useState<RuleStatusTab>("All");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const gridApiRef = useRef<GridApi<RoutingRule> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [viewingRule, setViewingRule] = useState<RoutingRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<RoutingRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => {
    gridApiRef.current?.paginationGoToFirstPage();
  }, [debouncedSearch, ruleStatusTab, tab]);
  useEffect(() => {
    if (isMerchantUser) return;
    fetchActiveMerchants()
      .then((list) => setMerchants(list))
      .catch(() => {
        setMerchantsFailed(true);
        showToast("Failed to load merchants.", "error");
      });
  }, [isMerchantUser]);
  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rulesData = await fetchRoutingRules();
      setRules(rulesData);
    } catch (error: any) {
      setLoadError(error?.message || "Failed to load routing rules.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadAll();
  }, [loadAll]);
  const filteredRules = useMemo(
    () =>
      filterRoutingRules(rules, {
        search: debouncedSearch,
        status: ruleStatusTab,
      }),
    [rules, debouncedSearch, ruleStatusTab],
  );
  const handleToggle = useCallback(
    async (rule: RoutingRule) => {
      const previous = rules;
      const nextActive = !rule.active;
      const statusLabel = nextActive ? 'Active' : 'Inactive';
      setRules((current) =>
        current.map((r) =>
          r.id === rule.id ? { ...r, active: nextActive } : r,
        ),
      );
      try {
        await setRoutingRuleActive(rule.id, nextActive);
        showToast(`${rule.name} status updated to ${statusLabel}.`, 'success');
      } catch (error: any) {
        setRules(previous);
        showToast(error?.message || 'Failed to update rule.', 'error');
      }
    },
    [rules],
  );
  const handleDelete = async () => {
    if (!deletingRule) return;
    setDeleting(true);
    try {
      await deleteRoutingRule(deletingRule.id);
      showToast("Routing rule deleted.", "success");
      setDeletingRule(null);
      loadAll();
    } catch (error: any) {
      showToast(error?.message || "Failed to delete rule.", "error");
    } finally {
      setDeleting(false);
    }
  };
  const handleSubmit = async (payload: RoutingRulePayload) => {
    try {
      if (editingRule) {
        await updateRoutingRule(editingRule.id, payload);
        showToast("Routing rule updated.", "success");
      } else {
        await createRoutingRule(payload);
        showToast("Routing rule created.", "success");
      }
      setDialogOpen(false);
      setEditingRule(null);
      loadAll();
    } catch (error: any) {
      showToast(error?.message || "Failed to save rule.", "error");
    }
  };
  const routingColumnDefs = useMemo<ColDef<RoutingRule>[]>(
    () => createRoutingRuleColumnDefs(!isMerchantUser),
    [isMerchantUser],
  );
  const routingGridContext = useMemo<RoutingRuleGridContext>(
    () => ({
      actions: {
        onStatus: handleToggle,
        onEdit: (rule: RoutingRule) => {
          setEditingRule(rule);
          setDialogOpen(true);
        },
        onDelete: (rule: RoutingRule) => setDeletingRule(rule),
        statusLabel: 'Change Status',
      },
      onView: (rule: RoutingRule) => setViewingRule(rule),
    }),
    [handleToggle],
  );
  const routingGridOptions = useMemo(
    () => ({
      ...routingRuleGridOptions,
      overlayNoRowsTemplate:
        filteredRules.length === 0 && rules.length > 0
          ? '<span class="text-sm text-muted-foreground">No routing rules match the current filters.</span>'
          : routingRuleGridOptions.overlayNoRowsTemplate,
    }),
    [filteredRules.length, rules.length],
  );
  const handleGridReady = useCallback(
    (event: { api: GridApi<RoutingRule> }) => {
      gridApiRef.current = event.api;
    },
    [],
  );
  const getRuleStatusCount = useCallback(
    (tab: StatusFilterTab) => {
      const key = typeof tab === 'string' ? tab : tab.value;
      if (key === 'All') return rules.length;
      if (key === 'Active') return rules.filter((rule) => rule.active).length;
      return rules.filter((rule) => !rule.active).length;
    },
    [rules],
  );
  const gridEmptyMessage =
    rules.length === 0
      ? "No routing rules found."
      : "No routing rules match the current filters.";
  return (
    <ListConsolePage
      title="Payment Routing"
      error={loadError}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <StatusFilterTabs
            tabs={[...PAYMENT_ROUTING_VIEW_TABS]}
            value={tab}
            onChange={(value) => setTab(value as TabKey)}
            disabled={loading}
            className="w-auto shrink-0"
          />
          {tab === 'rules' ? (
            <Button
              type="button"
              size="sm"
              className="tm-console-action-btn gap-1.5"
              onClick={() => {
                setEditingRule(null);
                setDialogOpen(true);
              }}
            >
              <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
              New Rule
            </Button>
          ) : null}
        </div>
      }
    >
      <ListConsoleGridSection
        hideToolbar={tab === 'simulator'}
        toolbarLeft={
          tab === 'rules' ? (
            <StatusFilterTabs
              tabs={[...RULE_STATUS_TABS]}
              value={ruleStatusTab}
              onChange={(value) => setRuleStatusTab(value as RuleStatusTab)}
              disabled={loading}
              getCount={getRuleStatusCount}
              className="w-auto shrink-0"
            />
          ) : undefined
        }
        toolbarRight={
          tab === 'rules' ? (
            <div className="tm-toolbar-controls">
              <GridSearchInput
                value={search}
                onValueChange={setSearch}
                placeholder="Search rule, merchant, or MID…"
                disabled={loading}
                className="tm-toolbar-search"
                aria-label="Search routing rules"
              />
            </div>
          ) : undefined
        }
      >
        {tab === 'rules' ? (
          <Card className="overflow-hidden border-0 shadow-none">
            <p className="border-b border-border px-4 py-3 text-sm text-muted-foreground">
              An active routing rule is a merchant&apos;s sole router: it supersedes priority routing
              and any terminal id. Groups are checked top to bottom and the first whose
              country/currency/amount all match wins (single attempt — an acquirer decline is
              final). If nothing matches, the rule&apos;s global fallback is used, or the payment is
              rejected.
            </p>
            <DataGrid<RoutingRule>
              rowData={filteredRules}
              columnDefs={routingColumnDefs}
              context={routingGridContext}
              defaultColDef={ROUTING_GRID_DEFAULT_COL_DEF}
              gridOptions={{
                ...routingGridOptions,
                overlayNoRowsTemplate: `<span class="text-sm text-muted-foreground">${gridEmptyMessage}</span>`,
              }}
              includeAuditColumns={false}
              loading={loading}
              pagination
              paginationPageSize={GRID_PAGE_SIZE}
              paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
              height="100%"
              className={LIST_CONSOLE_AG_GRID_CLASS}
              onGridReady={handleGridReady}
            />
          </Card>
        ) : (
          <SimulatorPanel
            merchants={merchants}
            isMerchantUser={isMerchantUser}
            merchantsFailed={merchantsFailed}
          />
        )}
      </ListConsoleGridSection>
      {dialogOpen && (
        <RoutingRuleDialog
          rule={editingRule}
          merchants={merchants}
          isMerchantUser={isMerchantUser}
          onCancel={() => {
            setDialogOpen(false);
            setEditingRule(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
      {viewingRule && (
        <RoutingRuleViewDialog
          rule={viewingRule}
          onClose={() => setViewingRule(null)}
          onSaved={() => {
            loadAll();
          }}
        />
      )}
      {deletingRule && (
        <DeleteConfirmDialog
          open={!!deletingRule}
          title="Delete routing rule"
          entityLabel="routing rule"
          entityName={deletingRule.name}
          description="This cannot be undone. Payments will fall back to the merchant's existing acquirer order."
          details={[
            { label: "Merchant", value: deletingRule.merchantName ?? "—" },
            { label: "Rule", value: deletingRule.name },
            {
              label: "Condition groups",
              value: String(deletingRule.groups?.length ?? 0),
            },
            {
              label: "Routes to",
              value: deletingRule.groups?.[0]?.targetMidLabel ?? "—",
            },
          ]}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setDeletingRule(null)}
        />
      )}
    </ListConsolePage>
  );
}
function SimulatorPanel({
  merchants,
  isMerchantUser,
  merchantsFailed,
}: {
  merchants: MerchantOption[];
  isMerchantUser: boolean;
  merchantsFailed: boolean;
}) {
  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [countryCode, setCountryCode] = useState("DE");
  const [currency, setCurrency] = useState("EUR");
  const [amount, setAmount] = useState("100");
  const [deviceType, setDeviceType] = useState("");
  const [firedRiskRules, setFiredRiskRules] = useState<string[]>([]);
  const [riskFields, setRiskFields] = useState<RiskField[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchRiskFields()
      .then((fields) => {
        if (!cancelled) setRiskFields(fields);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const [result, setResult] = useState<RoutingSimulation | null>(null);
  const [running, setRunning] = useState(false);
  const scopeReady = isMerchantUser || merchantId !== null;
  const simulationColumnDefs = useMemo(
    () => createSimulationStepColumnDefs(),
    [],
  );
  const simulationGridOptions = useMemo(
    () => ({
      ...simulationStepGridOptions,
      getRowClass: (params: { data?: { selected?: boolean } }) =>
        params.data?.selected ? "bg-[rgba(46,213,115,0.08)]" : undefined,
    }),
    [],
  );
  const run = async () => {
    setRunning(true);
    try {
      const simulation = await simulateRouting(
        {
          countryCode,
          currency,
          amount: Number(amount),
          deviceType: deviceType || undefined,
          firedRiskRules: firedRiskRules.length > 0 ? firedRiskRules : undefined,
        },
        merchantId,
      );
      setResult(simulation);
    } catch (error: any) {
      showToast(error?.message || "Simulation failed.", "error");
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="p-4">
      <p className="mb-3 text-sm text-muted-foreground">
        Runs against the same engine the live payment path uses — this is what
        would actually happen.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        {!isMerchantUser && (
          <div className="flex flex-col gap-1.5">
            <Label>Merchant</Label>
            <div className="w-56">
              <SearchableSelect
                className="h-9"
                value={merchantId != null ? String(merchantId) : ""}
                options={merchants.map((m) => ({
                  value: String(m.id),
                  label: m.name,
                }))}
                onChange={(value) => setMerchantId(Number(value) || null)}
                placeholder={
                  merchantsFailed
                    ? "Failed to load merchants"
                    : "Select merchant"
                }
                searchPlaceholder="Search merchant…"
                emptyMessage={
                  merchantsFailed
                    ? "Failed to load merchants"
                    : "No merchants available"
                }
                aria-label="Merchant"
              />
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sim-country">Country (ISO or name)</Label>
          <Input
            id="sim-country"
            className="h-9 w-40"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sim-currency">Currency</Label>
          <Input
            id="sim-currency"
            className="h-9 w-32"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sim-amount">Amount</Label>
          <Input
            id="sim-amount"
            type="number"
            min="0"
            className="h-9 w-32"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sim-device">Device</Label>
          <Select
            value={deviceType || 'ANY'}
            onValueChange={(value) => setDeviceType(value === 'ANY' ? '' : value)}
          >
            <SelectTrigger id="sim-device" className="h-9 w-36" aria-label="Device">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              {SIM_DEVICE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          className="tm-console-action-btn"
          onClick={run}
          disabled={running || !scopeReady}
        >
          {running ? 'Running…' : 'Simulate'}
        </Button>
      </div>
      {riskFields.length > 0 && (
        <div className="mt-3 flex flex-col gap-1.5">
          <Label>Risk rules fired (optional)</Label>
          <div className="flex flex-wrap gap-2">
            {riskFields.map((rf) => {
              const on = firedRiskRules.includes(rf.value);
              return (
                <button
                  key={rf.value}
                  type="button"
                  title={rf.value}
                  aria-pressed={on}
                  onClick={() =>
                    setFiredRiskRules((cur) =>
                      cur.includes(rf.value)
                        ? cur.filter((v) => v !== rf.value)
                        : [...cur, rf.value],
                    )
                  }
                  className={
                    'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ' +
                    (on
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-transparent text-muted-foreground hover:text-foreground')
                  }
                >
                  {rf.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Toggle the risk rules to pretend fired for this test payment, to check risk-based routing.
          </p>
        </div>
      )}
      {result && (
        <div className="mt-4 space-y-3">
          <div
            className={`rounded-lg px-3.5 py-3 text-sm ${
              result.matched
                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                : "bg-muted/40 text-muted-foreground"
            }`}
          >
            {result.explanation}
          </div>
          {result.steps?.length > 0 && (
            <DataGrid
              rowData={result.steps}
              columnDefs={simulationColumnDefs}
              includeAuditColumns={false}
              gridOptions={simulationGridOptions}
              pagination={false}
              height={280}
              className={LIST_CONSOLE_AG_GRID_CLASS}
            />
          )}
        </div>
      )}
    </div>
  );
}
